import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import { useConnectionHealthContext } from "../../context/ConnectionHealthContext";
import type { ConnectionAlert } from "../../types/socialConnections";

// Where the reconnect flow lives. "/connect" is only a redirect that drops
// router state, so target the real tab URL.
const CONNECT_PATH = "/social-posts?tab=connect";

const PLATFORM_LABEL: Record<string, string> = {
  LINKEDIN: "LinkedIn",
  LINKEDIN_BUSINESS: "LinkedIn Page",
  FACEBOOK: "Facebook",
  INSTAGRAM: "Instagram",
  YOUTUBE: "YouTube",
  GMB: "Google Business",
  BING_WEBMASTER: "Bing Webmaster",
};

/** Connect-page section to land on, for platforms not on the first one. */
const PLATFORM_SECTION: Record<string, string> = {
  BING_WEBMASTER: "search",
};

/** The row's status line and button. A key due for replacing is not a broken
 *  connection, so it says what to do rather than "reconnect". */
function alertCopy(alert: ConnectionAlert): { status: string; action: string } {
  if (alert.reason === "rotate_key") {
    return { status: "Time to refresh your Bing key", action: "Update key" };
  }
  return {
    status: alert.level === "red" ? "Reconnect required" : "Reconnect soon",
    action: "Reconnect",
  };
}

function platformLabel(platform: string): string {
  return PLATFORM_LABEL[(platform || "").toUpperCase()] || platform;
}

/**
 * Which ConnectPage OAuth flow reconnects this profile. LinkedIn splits into
 * personal vs Page: a Page id is a full urn:li:organization: URN, a personal id
 * is the bare member sub. Facebook / Instagram map straight to their platform key
 * (Instagram publishes through the linked Page, so its reconnect is Meta OAuth).
 * Bing maps straight through too; its "reconnect" opens the API-key panel.
 */
function reconnectPlatformKey(alert: ConnectionAlert): string {
  if (
    alert.platform.toUpperCase() === "LINKEDIN" &&
    alert.profileId.startsWith("urn:li:organization:")
  ) {
    return "LINKEDIN_BUSINESS";
  }
  return alert.platform.toUpperCase();
}

const MENU_POSITION_CLASS: Record<string, string> = {
  "below-right": "right-0 top-full mt-1",
  "below-left": "left-0 top-full mt-1",
  "right-start": "left-full top-0 ml-2",
};

/**
 * App-shell alarm bell for social connections that can no longer publish
 * (SGL-22). Red = reconnect required now (the token is dead); amber = reconnect
 * soon (an unrecoverable expiry is near — LinkedIn's 1-year refresh-token wall,
 * or a Facebook/Instagram token's own ~60-day expiry). "Reconnect" re-runs the
 * existing per-platform OAuth flow on the Connect page.
 *
 * Data comes from ConnectionHealthProvider (one poll for the whole app). Renders
 * for every role; shows nothing but a plain bell when everything is healthy.
 */
export default function ConnectionAlertBell({
  className = "",
  menuPosition = "below-right",
}: {
  className?: string;
  /** "below-right" (mobile header, far right), "below-left" (expanded sidebar
   *  header), "right-start" (collapsed icon rail). */
  menuPosition?: "below-right" | "below-left" | "right-start";
}): JSX.Element | null {
  const { isAuthenticated } = useAuth();
  const { selectedCustomerId } = useCustomerWorkspace();
  const navigate = useNavigate();
  const { alerts, redCount, amberCount } = useConnectionHealthContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!isAuthenticated || !selectedCustomerId) return null;

  const total = redCount + amberCount;

  const handleReconnect = (alert: ConnectionAlert) => {
    setOpen(false);
    const section = PLATFORM_SECTION[alert.platform.toUpperCase()];
    navigate(section ? `${CONNECT_PATH}&section=${section}` : CONNECT_PATH, {
      state: {
        reconnectPlatform: reconnectPlatformKey(alert),
        reconnectProfileId: alert.profileId,
      },
    });
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-theme-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
        aria-label={
          total > 0
            ? `${total} social connection${total > 1 ? "s" : ""} need attention`
            : "Social connections"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="material-icons-outlined text-xl" aria-hidden>
          notifications
        </span>
        {total > 0 && (
          <span
            className={`absolute -right-0.5 -top-0.5 flex min-w-[15px] items-center justify-center rounded-full px-[3px] text-[10px] font-bold leading-[15px] text-white ${
              redCount > 0 ? "bg-red-600" : "bg-amber-500"
            }`}
          >
            {total}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-[100] w-72 rounded-xl border border-theme-border bg-theme-surface p-1 shadow-[0_12px_40px_-4px_rgba(0,0,0,0.14)] ring-1 ring-theme-border/90 dark:shadow-[0_12px_40px_-4px_rgba(0,0,0,0.45)] ${
            MENU_POSITION_CLASS[menuPosition] ?? MENU_POSITION_CLASS["below-right"]
          }`}
          role="group"
          aria-label="Social connection alerts"
        >
          <p className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
            Social connections
          </p>
          {alerts.length === 0 ? (
            <p className="px-2 py-2 text-xs text-theme-fg-muted">
              All connections healthy.
            </p>
          ) : (
            alerts.map((alert) => {
              const copy = alertCopy(alert);
              return (
              <div
                key={`${alert.platform}:${alert.profileId}`}
                className="flex items-center gap-2 rounded-md px-2 py-1.5"
              >
                <span
                  className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                    alert.level === "red" ? "bg-red-600" : "bg-amber-500"
                  }`}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-theme-fg">
                    {alert.name}
                  </p>
                  <p className="text-[11px] text-theme-fg-muted">
                    {platformLabel(alert.platform)} · {copy.status}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleReconnect(alert)}
                  className="shrink-0 rounded-md bg-blue-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-800"
                >
                  {copy.action}
                </button>
              </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
